import { ClientLogger } from 'bernie-client';
import { SerializedData } from 'bernie-core';
import { Request } from 'bernie-http';
import { Logger, NOOP_LOGGER } from 'bernie-logger';
import { Store } from 'bernie-plugin-mobx';

import { getNetwork, Network } from 'modules/network';
import { getProgram, ProductId, Program, ProgramId } from 'modules/program';
import { getTracking, Tracking } from 'modules/tracking';

type TAnalyticData = {
  referrerIdPrefix: string;
  linkNamePrefix: string;
  applicationName: string;
};

class AppStore extends Store {
  public routeParams: Record<string, string> = {};
  public routeQueries: Record<string, string> = {};

  public productId: ProductId;

  public program: Program;
  public network: Network;
  public tracking: Tracking;

  public analyticData: TAnalyticData = {
    referrerIdPrefix: '',
    linkNamePrefix: '',
    applicationName: '',
  };

  public constructor(state: SerializedData = {}, logger: Logger | ClientLogger = NOOP_LOGGER, request: Request = null) {
    super(state, logger);

    if (request) {
      this.setRouteParams(request.params || {});
      this.setRouteQueries(request.query || {});

      const programId = this.routeQueries['program'] as ProgramId;
      this.setProgramById(programId);
      this.setNetworkFromRouteQuery();
    }
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  public setRouteParams(value: Record<string, string>): void {
    this.routeParams = value;
  }

  public setRouteQueries(value: Record<string, string>): void {
    this.routeQueries = value;
  }

  public setProgramById(programId: ProgramId): void {
    this.program = getProgram({ programId });
  }

  // default to Pz
  public setNetworkFromRouteQuery(): void {
    const network = getNetwork({ networkId: this.routeQueries['network'] || 'pz' });
    this.network = network;
    this.setAnalyticData(network?.id);
  }

  public setAnalyticData(networkId: string): void {
    let analyticData: TAnalyticData;

    switch (networkId) {
      case 'direct':
        analyticData = {
          referrerIdPrefix: 'managed',
          linkNamePrefix: 'Managed App',
          applicationName: 'managed-app',
        };
        break;

      default:
      case 'pz':
        analyticData = {
          referrerIdPrefix: 'affhub',
          linkNamePrefix: 'AffHub',
          applicationName: 'affiliate-hub',
        };
        break;
    }

    this.analyticData = analyticData;
  }

  // To be invoked during 'fetch' of each product controller
  public setProductId(value: ProductId): void {
    this.productId = value;
  }

  // To be invoked during 'fetch' (after setProductId) of each product controller
  public setTracking(): void {
    const baseTracking = getTracking({
      programId: this.program?.id,
      networkId: this.network?.id,
      productId: this.productId,
    });
    const extTracking = {
      camRef: this.routeQueries['camref'],
      pubRef: this.routeQueries['pubref'],
      adRef: this.routeQueries['adref'],
      mdpcid: this.routeQueries['mdpcid'],
      rffrid: this.routeQueries['rffrid'],
    };

    this.tracking = { ...baseTracking, ...extTracking };
  }
}

export { AppStore };
